import { isEqual } from 'lodash';
import moment from 'moment';
import {
  Observable,
  Subject,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
} from 'rxjs';

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

type TimeFrameType = 'd' | 'M' | 'w';
type TimeFrameMode = 'relative' | 'specific';

const RELATIVE_TIMEFRAME_REGEX = /^(\d+)([dMw])$/;

interface TimeFrameFrequencyOption {
  label: string;
  value: TimeFrameType;
}

type EvaluatedTimeFrame = ReturnType<
  typeof TimeFrameComponent.prototype.timeFrameFormGroup.getRawValue
>;

@Component({
  selector: 'phoenix-time-frame',
  templateUrl: './time-frame.component.html',
  standalone: false,
})
export class TimeFrameComponent implements OnInit, OnChanges, OnDestroy {
  @Input() helperText = '';
  @Input() freeText = false;
  @Input() timeFrame = new FormControl<string | null>(null);
  @Input() title = 'Report Timeframe';
  @Input() firstCheck = new Observable<void>();

  @Output() timeFrameChange = new EventEmitter<string | null>();

  destroy$ = new Subject<void>();
  private controlDestroy$ = new Subject<void>();

  allowTimeFrame = new FormControl(false, { nonNullable: true });
  timeFrameType = new FormControl<TimeFrameMode>('relative', {
    nonNullable: true,
  });

  timeFrameFormGroup = new FormGroup({
    relativeText: new FormControl('', {
      nonNullable: true,
    }),
    timeFrameNum: new FormControl<number>(0, {
      nonNullable: true,
    }),
    timeFrameFrequency: new FormControl<TimeFrameType>('d', {
      nonNullable: true,
    }),
    timeFrameDate: new FormControl<string | null>(null),
  });

  frequencyOptions: TimeFrameFrequencyOption[] = [
    {
      label: 'Day',
      value: 'd',
    },
    {
      label: 'Month',
      value: 'M',
    },
    {
      label: 'Week',
      value: 'w',
    },
  ];

  formReset() {
    this.timeFrameFormGroup.reset();
    this.timeFrame.patchValue('');
  }

  ngOnInit(): void {
    this.firstCheck.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.firstValuesCheck();
    });
  }

  firstValuesCheck() {
    this.evaluateRadio();
    this.patchTimeFrameValue();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['timeFrame']) {
      this.controlDestroy$.next();

      this.timeFrameFormGroup.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          takeUntil(this.controlDestroy$)
        )
        .subscribe(() => {
          this.changeTimeFrame();
        });

      this.allowTimeFrame.valueChanges
        .pipe(takeUntil(this.controlDestroy$))
        .subscribe(() => {
          this.syncTimeFrameRequiredError();
        });

      this.timeFrame.valueChanges
        .pipe(takeUntil(this.controlDestroy$))
        .subscribe(() => {
          this.patchTimeFrameValue();
          this.syncTimeFrameRequiredError();
        });

      this.timeFrame.statusChanges
        .pipe(takeUntil(this.controlDestroy$))
        .subscribe(() => {
          this.syncDisabledState();
        });
    }
  }

  private syncDisabledState(): void {
    if (this.timeFrame.disabled) {
      this.timeFrameFormGroup.disable();
    } else {
      this.timeFrameFormGroup.enable();
    }
  }

  private syncTimeFrameRequiredError(): void {
    const isEmpty = !this.timeFrame.value;
    if (this.allowTimeFrame.value && isEmpty) {
      this.timeFrame.setErrors({ required: true }, { emitEvent: false });
    } else {
      this.timeFrame.setErrors(null, { emitEvent: false });
    }
  }

  patchTimeFrameValue() {
    const data = this.evaluateTimeframe();
    if (!isEqual(this.timeFrameFormGroup.getRawValue(), data)) {
      this.timeFrameFormGroup.patchValue(data);
    }
  }

  evaluateTimeframe(value = this.timeFrame.value): EvaluatedTimeFrame {
    const emptyValue: EvaluatedTimeFrame = {
      relativeText: '',
      timeFrameNum: 0,
      timeFrameFrequency: 'd',
      timeFrameDate: null,
    };

    if (!value) {
      return emptyValue;
    }

    if (this.freeText) {
      if (this.isIsoDate(value)) {
        return {
          ...emptyValue,
          timeFrameDate: value,
        };
      }

      return {
        ...emptyValue,
        relativeText: value,
      };
    }

    const relativeMatch = RELATIVE_TIMEFRAME_REGEX.exec(value);

    if (relativeMatch) {
      return {
        ...emptyValue,
        timeFrameNum: Number(relativeMatch[1]),
        timeFrameFrequency: relativeMatch[2] as TimeFrameType,
      };
    }

    return {
      ...emptyValue,
    };
  }

  private isIsoDate(value: string | null): boolean {
    if (!value) {
      return false;
    }

    return moment(value, moment.ISO_8601, true).isValid();
  }

  evaluateRadio(value = this.timeFrame.value): void {
    this.allowTimeFrame.setValue(!!value);

    let radioValue!: TimeFrameMode;

    if (this.freeText) {
      radioValue = this.isIsoDate(value) ? 'specific' : 'relative';
    } else {
      radioValue =
        value && RELATIVE_TIMEFRAME_REGEX.exec(value) ? 'relative' : 'specific';
    }

    this.timeFrameType.setValue(radioValue);
  }

  changeTimeFrame(): void {
    const value = this.getTimeFrameValue();

    if (this.timeFrame.value !== value) {
      this.timeFrame.setValue(value);
      this.timeFrame.markAsDirty();
      this.timeFrameChange.emit(value);
    }
  }

  private getTimeFrameValue(): string | null {
    const data = this.timeFrameFormGroup.getRawValue();

    if (!this.allowTimeFrame.value) {
      return '';
    }

    if (this.timeFrameType.value === 'specific') {
      return data.timeFrameDate;
    }

    if (this.freeText && this.timeFrameType.value === 'relative') {
      return data.relativeText;
    }

    if (this.timeFrameType.value === 'relative') {
      if (!data.timeFrameNum || !data.timeFrameFrequency) {
        return '';
      }

      return `${data.timeFrameNum}${data.timeFrameFrequency}`;
    }

    return '';
  }

  ngOnDestroy(): void {
    this.controlDestroy$.next();
    this.controlDestroy$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
